import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, IndianRupee, User, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";

export default function CoursesPage() {
  const { settings } = useSettings();
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => setCourses([]));
  }, []);

  const cats = useMemo(() => ["all", ...Array.from(new Set(courses.map((c) => c.category).filter(Boolean)))], [courses]);
  const filtered = useMemo(() => courses.filter((c) =>
    (cat === "all" || c.category === cat) &&
    (q === "" || (c.title + c.description).toLowerCase().includes(q.toLowerCase()))
  ), [courses, q, cat]);

  const signup = settings?.student_portal?.signup || "https://course.eqtech.academy/signup.php";

  return (
    <div className="pt-16 pb-24" data-testid="courses-page">
      <div className="container-eq">
        <div className="max-w-2xl">
          <div className="label-eq text-sky-500 mb-3">All Courses</div>
          <h1 className="text-4xl lg:text-6xl font-display font-black tracking-tighter" data-testid="courses-title">Learn the tools that get you hired.</h1>
          <p className="mt-5 text-neutral-600 text-lg">Live cohorts, AI-integrated curriculum, and career mentorship. Pick a program that matches your goal.</p>
        </div>

        <div className="mt-10 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input placeholder="Search courses..." value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full border border-neutral-200 bg-white focus:outline-none focus:border-sky-500"
              data-testid="courses-search" />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm capitalize whitespace-nowrap transition-colors ${cat === c ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-100"}`}
                data-testid={`courses-filter-${c}`}>{c}</button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => {
            const enrollUrl = c.purchase_url || signup;
            return (
              <div key={c.id} className="card-eq overflow-hidden group flex flex-col" data-testid={`course-${c.slug}`}>
                <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                  {c.thumbnail && <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="text-xs uppercase tracking-widest text-sky-500 font-semibold">{c.category}</div>
                  <h3 className="font-display font-bold text-lg mt-2">{c.title}</h3>
                  <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{c.description}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-neutral-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {c.duration}</span>
                    <span className="inline-flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> {(c.fees || "").replace(/^₹/, "")}</span>
                    {c.trainer && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {c.trainer}</span>}
                  </div>
                  <div className="mt-5 flex gap-2 mt-auto">
                    <Link to={`/courses/${c.slug}`} className="btn-outline-eq flex-1 justify-center text-sm py-2" data-testid={`course-details-${c.slug}`}>Details</Link>
                    <a href={enrollUrl} target="_blank" rel="noreferrer"
                       className="btn-primary-eq flex-1 justify-center text-sm py-2" data-testid={`course-enroll-${c.slug}`}>
                      Enroll <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
